/* "Add to Home Screen" nudge — Mission Control should feel like a native
   app on the founder's phone. Shows once on mobile browsers that aren't
   already standalone: Chromium gets the real install prompt via
   beforeinstallprompt; iOS Safari gets the Share → Add to Home Screen hint
   (no API exists). Dismiss persists per device. */
import { useEffect, useState } from 'react';
import { Button } from '../../ds';
import { useIsMobile } from '../useBreakpoint';

const KEY = 'mc.pwa.nudge.dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

export function PwaNudge() {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);
  const [installEvt, setInstallEvt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (!isMobile || isStandalone()) return;
    try {
      if (localStorage.getItem(KEY)) return;
    } catch {
      return;
    }
    // Chromium path: capture the install prompt if the browser offers one.
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    // Show after a beat either way (iOS never fires the event).
    const t = setTimeout(() => setVisible(true), 4000);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      clearTimeout(t);
    };
  }, [isMobile]);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(KEY, '1');
    } catch {
      /* private mode */
    }
  };

  const install = async () => {
    if (installEvt) {
      await installEvt.prompt();
      dismiss();
    }
  };

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <div
      role="status"
      style={{
        position: 'fixed', left: 12, right: 12, bottom: 'calc(var(--mc-bottombar-h) + 10px)',
        zIndex: 'var(--z-toast)' as unknown as number,
        background: 'var(--surface)', border: '1px solid var(--primary-line)', borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 11,
      }}
    >
      <span aria-hidden="true" style={{ width: 34, height: 34, flex: 'none', borderRadius: 9, display: 'grid', placeItems: 'center', background: 'var(--primary-soft)', border: '1px solid var(--primary-line)', color: 'var(--primary)' }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <rect x={5} y={2} width={14} height={20} rx={2.5} />
          <path d="M12 18h.01" />
        </svg>
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45, color: 'var(--ink)' }}>
        <b style={{ fontWeight: 600 }}>Put Mission Control on your Home Screen.</b>{' '}
        <span style={{ color: 'var(--muted)' }}>
          {installEvt ? 'One tap — it opens like a native app.' : isIos ? 'Share → “Add to Home Screen”.' : 'Use your browser’s “Add to Home Screen”.'}
        </span>
      </span>
      {installEvt && (
        <Button size="sm" onClick={() => void install()}>Install</Button>
      )}
      <button onClick={dismiss} aria-label="Dismiss install hint" style={{ flex: 'none', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 8, lineHeight: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
